import {useState} from 'react'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {Modal, Button, Table, Form, Badge, Alert, Spinner} from 'react-bootstrap'
import {useQuery, useMutation, useLazyQuery} from '@apollo/client'
import {toast} from 'react-toastify'
import {GET_ALL_TAGS, GET_TAG_USAGE} from '../../../gql/queries/tagsQuery'
import {GET_ALL_ORGANIZATIONS} from '../../../gql/queries/organizationsQuery'
import {ADD_EDIT_TAG, DELETE_TAG_INSURANCE} from '../../../gql/mutations/tagMutations'
import {IResTags, IResAddTag, IAllTagNode, IResTagUsage, ITagUsage, IResDeleteTag} from '../../../types/tags'
import {useGetUser} from '../../../store/userStore'
import {parseId} from '../../../helpers'

const getContrastColor = (hex: string): string => {
  const clean = (hex || '').replace('#', '')
  const r = parseInt(clean.substring(0, 2), 16)
  const g = parseInt(clean.substring(2, 4), 16)
  const b = parseInt(clean.substring(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5 ? '#000000' : '#ffffff'
}

interface LeadTagManagerProps {
  show: boolean
  onHide: () => void
  onTagsUpdated?: () => void
}

interface IOrgNode {
  id: string
  name: string
}

interface IResOrganizations {
  allOrganizations: {edges: {node: IOrgNode}[]}
}

/** Confirmacion pendiente: editar un tag global compartido, o borrar uno. */
type PendingConfirm = {
  mode: 'edit' | 'delete'
  tag: IAllTagNode
  usage: ITagUsage
}

const requiredMark = '*'
const colonSpace = ': '
const emptyForm = {name: '', color: '#3699ff', organizationId: ''}

const tM = defineMessages({
  title: {id: 'leads.tags.title', defaultMessage: 'Manage tags'},
  name: {id: 'leads.tags.name', defaultMessage: 'Tag name'},
  namePlaceholder: {id: 'leads.tags.namePlaceholder', defaultMessage: 'e.g., Hot Lead, Follow Up'},
  color: {id: 'common.field.color', defaultMessage: 'Color'},
  create: {id: 'leads.tags.create', defaultMessage: 'Create tag'},
  update: {id: 'leads.tags.update', defaultMessage: 'Update tag'},
  existing: {id: 'leads.tags.existing', defaultMessage: 'Existing tags ({count})'},
  empty: {id: 'leads.tags.empty', defaultMessage: 'No tags created yet. Create your first tag above.'},
  scope: {id: 'leads.tags.scope', defaultMessage: 'Scope'},
  global: {id: 'leads.tags.global', defaultMessage: 'Global'},
  scopeHelp: {
    id: 'leads.tags.scopeHelp',
    defaultMessage: 'Global tags are visible to every organization, but only a SuperAdmin can edit them.',
  },
  editTooltip: {id: 'leads.tags.editTooltip', defaultMessage: 'Edit tag'},
  deleteTooltip: {id: 'leads.tags.deleteTooltip', defaultMessage: 'Delete tag'},
  noPermEdit: {id: 'leads.tags.noPermEdit', defaultMessage: "You don't have permission to edit this tag"},
  noPermDelete: {id: 'leads.tags.noPermDelete', defaultMessage: "You don't have permission to delete this tag"},
  confirmEditTitle: {id: 'leads.tags.confirmEditTitle', defaultMessage: 'Edit a shared global tag'},
  confirmEditBody: {
    id: 'leads.tags.confirmEditBody',
    defaultMessage:
      'This tag is global and is already applied to leads from {orgs} organizations. Any change to its name, color or scope affects all of them.',
  },
  confirmDeleteTitle: {id: 'leads.tags.confirmDeleteTitle', defaultMessage: 'Delete tag'},
  confirmDeleteUnused: {
    id: 'leads.tags.confirmDeleteUnused',
    defaultMessage: 'This tag is not applied to any lead. Deleting it cannot be undone.',
  },
  confirmDeleteInUse: {
    id: 'leads.tags.confirmDeleteInUse',
    defaultMessage:
      'This tag is still in use. It will first be unlinked from everything below and then permanently deleted. This cannot be undone.',
  },
  usageLeads: {
    id: 'leads.tags.usageLeads',
    defaultMessage: '{count, plural, one {# lead} other {# leads}}',
  },
  usageAutoPush: {
    id: 'leads.tags.usageAutoPush',
    defaultMessage: '{count, plural, one {# auto-push rule} other {# auto-push rules}}',
  },
  usageWebhooks: {
    id: 'leads.tags.usageWebhooks',
    defaultMessage: '{count, plural, one {# lead webhook} other {# lead webhooks}}',
  },
  usageNoOrg: {id: 'leads.tags.usageNoOrg', defaultMessage: 'Pipelines without organization'},
  checkingUsage: {id: 'leads.tags.checkingUsage', defaultMessage: 'Checking where this tag is used…'},
  continueAction: {id: 'leads.tags.continueAction', defaultMessage: 'Continue'},
  deleteAnyway: {id: 'leads.tags.deleteAnyway', defaultMessage: 'Unlink and delete'},
  toastCreated: {id: 'leads.tags.toast.created', defaultMessage: 'Tag created successfully'},
  toastUpdated: {id: 'leads.tags.toast.updated', defaultMessage: 'Tag updated successfully'},
  toastDeleted: {id: 'leads.tags.toast.deleted', defaultMessage: 'Tag deleted successfully'},
  toastDeletedUnlinked: {
    id: 'leads.tags.toast.deletedUnlinked',
    defaultMessage: 'Tag deleted and unlinked from {count, plural, one {# lead} other {# leads}}',
  },
  toastSaveFailed: {id: 'leads.tags.toast.saveFailed', defaultMessage: 'Failed to save tag'},
  toastDeleteFailed: {id: 'leads.tags.toast.deleteFailed', defaultMessage: 'Failed to delete tag'},
  toastUsageFailed: {id: 'leads.tags.toast.usageFailed', defaultMessage: 'Could not check where this tag is used'},
})

export const LeadTagManager: React.FC<LeadTagManagerProps> = ({show, onHide, onTagsUpdated}) => {
  const intl = useIntl()
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const isSuperAdmin = userGroups.includes('SuperAdministrador')
  const currentOrgId = useGetUser((s) => s.user?.user?.organization?.id)

  const [editingTag, setEditingTag] = useState<IAllTagNode | null>(null)
  const [formData, setFormData] = useState(emptyForm)
  const [pending, setPending] = useState<PendingConfirm | null>(null)
  const [busyTagId, setBusyTagId] = useState<string | null>(null)

  const {data, loading, refetch} = useQuery<IResTags>(GET_ALL_TAGS, {
    skip: !show,
    fetchPolicy: 'no-cache',
  })

  // El selector de scope solo lo ve el SuperAdmin, asi que no gastamos la
  // consulta con nadie mas.
  const {data: orgsData} = useQuery<IResOrganizations>(GET_ALL_ORGANIZATIONS, {
    skip: !show || !isSuperAdmin,
    fetchPolicy: 'cache-first',
  })
  const organizations = orgsData?.allOrganizations?.edges.map(({node}) => node) || []

  const [loadUsage] = useLazyQuery<IResTagUsage>(GET_TAG_USAGE, {fetchPolicy: 'no-cache'})

  const invalidateTagsCache = (cache: import('@apollo/client').ApolloCache<any>) => {
    cache.evict({fieldName: 'allTagInsurances'})
    cache.gc()
  }

  const [gqlAddEditTag, {loading: saving}] = useMutation<IResAddTag>(ADD_EDIT_TAG, {
    update: (cache) => invalidateTagsCache(cache),
  })
  const [gqlDeleteTag] = useMutation<IResDeleteTag>(DELETE_TAG_INSURANCE, {
    update: (cache) => invalidateTagsCache(cache),
  })

  const tags = data?.allTagInsurances?.edges.map(({node}) => node) || []

  const canManage = (tag: IAllTagNode) => {
    if (isSuperAdmin) return true
    if (!tag.organization) return false
    return String(parseId(tag.organization.id)) === String(parseId(currentOrgId || ''))
  }

  const fetchUsage = async (tag: IAllTagNode): Promise<ITagUsage | null> => {
    setBusyTagId(tag.id)
    try {
      // useLazyQuery no rechaza la promesa: el fallo llega en `error`.
      const res = await loadUsage({variables: {id: parseId(tag.id)}})
      const usage = res.data?.tagInsuranceUsage
      if (res.error || !usage) {
        toast.error(res.error?.message || intl.formatMessage(tM.toastUsageFailed))
        return null
      }
      return usage
    } catch (error: any) {
      toast.error(error.message || intl.formatMessage(tM.toastUsageFailed))
      return null
    } finally {
      setBusyTagId(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const result = await gqlAddEditTag({
        variables: {
          input: {
            id: editingTag ? parseId(editingTag.id) : undefined,
            name: formData.name,
            color: formData.color,
            // El scope solo lo decide el SuperAdmin; para el resto el backend
            // fuerza su propia organizacion, asi que ni mandamos el campo.
            ...(isSuperAdmin ? {organization: formData.organizationId || null} : {}),
          },
        },
      })

      if (result.data?.tagInsuranceCreateupdateMutation?.tagInsurance?.id) {
        toast.success(intl.formatMessage(editingTag ? tM.toastUpdated : tM.toastCreated))
        setFormData(emptyForm)
        setEditingTag(null)
        refetch()
        onTagsUpdated?.()
      } else if (result.data?.tagInsuranceCreateupdateMutation?.errors?.length) {
        toast.error(result.data.tagInsuranceCreateupdateMutation.errors[0]?.messages?.[0] || intl.formatMessage(tM.toastSaveFailed))
      }
    } catch (error: any) {
      toast.error(error.message || intl.formatMessage(tM.toastSaveFailed))
    }
  }

  const startEditing = (tag: IAllTagNode) => {
    setEditingTag(tag)
    setFormData({
      name: tag.name,
      color: tag.color,
      organizationId: tag.organization ? String(parseId(tag.organization.id)) : '',
    })
  }

  const handleEdit = async (tag: IAllTagNode) => {
    // Un tag propio de la organizacion no se comparte con nadie: se edita directo.
    if (tag.organization) {
      startEditing(tag)
      return
    }

    // Es global: sin saber a cuantas organizaciones afecta no se abre el form.
    const usage = await fetchUsage(tag)
    if (!usage) return

    if (usage.crossesOrganizations) {
      setPending({mode: 'edit', tag, usage})
      return
    }
    startEditing(tag)
  }

  const handleDelete = async (tag: IAllTagNode) => {
    const usage = await fetchUsage(tag)
    if (!usage) return
    setPending({mode: 'delete', tag, usage})
  }

  const confirmDelete = async (tag: IAllTagNode) => {
    try {
      // force: el backend desvincula leads, reglas de auto-push y webhooks
      // dentro de una transaccion y despues borra el tag.
      const res = await gqlDeleteTag({variables: {id: parseId(tag.id), force: true}})
      const payload = res.data?.deleteTagInsurance

      if (!payload?.ok) {
        toast.error(intl.formatMessage(tM.toastDeleteFailed))
        return
      }

      const unlinked = payload.unlinkedLeads || 0
      toast.success(
        unlinked > 0
          ? intl.formatMessage(tM.toastDeletedUnlinked, {count: unlinked})
          : intl.formatMessage(tM.toastDeleted)
      )

      if (editingTag && editingTag.id === tag.id) handleCancel()
      refetch()
      onTagsUpdated?.()
    } catch (error: any) {
      toast.error(error.message || intl.formatMessage(tM.toastDeleteFailed))
    }
  }

  const handleConfirm = async () => {
    if (!pending) return
    const {mode, tag} = pending
    setPending(null)

    if (mode === 'edit') startEditing(tag)
    else await confirmDelete(tag)
  }

  const handleCancel = () => {
    setEditingTag(null)
    setFormData(emptyForm)
  }

  const orgCount = (usage: ITagUsage) => usage.organizations.filter((o) => o.organizationId).length

  const renderUsageBreakdown = (usage: ITagUsage) => (
    <ul className='mb-0 ps-3'>
      {usage.organizations.map((org) => (
        <li key={org.organizationId ?? 'no-org'}>
          {org.organizationName || intl.formatMessage(tM.usageNoOrg)}
          {colonSpace}
          {intl.formatMessage(tM.usageLeads, {count: org.leadCount})}
        </li>
      ))}
      {usage.autoPushRuleCount > 0 && (
        <li>{intl.formatMessage(tM.usageAutoPush, {count: usage.autoPushRuleCount})}</li>
      )}
      {usage.webhookCount > 0 && (
        <li>{intl.formatMessage(tM.usageWebhooks, {count: usage.webhookCount})}</li>
      )}
    </ul>
  )

  const editingGlobalShared = Boolean(editingTag && !editingTag.organization)

  return (
    <>
      <Modal show={show && !pending} onHide={onHide} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage(tM.title)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='tag-form mb-4'>
            {isSuperAdmin && editingGlobalShared && (
              <Alert variant='warning' className='py-2 small'>
                <i className='bi bi-exclamation-triangle me-2'></i>
                {intl.formatMessage(tM.scopeHelp)}
              </Alert>
            )}
            <Form onSubmit={handleSubmit}>
              <div className='d-flex align-items-end gap-2 flex-wrap'>
                <Form.Group style={{flex: '2 1 220px'}}>
                  <Form.Label className='mb-1 small'>{intl.formatMessage(tM.name)} {requiredMark}</Form.Label>
                  <Form.Control
                    type='text'
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder={intl.formatMessage(tM.namePlaceholder)}
                  />
                </Form.Group>
                <Form.Group style={{flex: '0 0 auto'}}>
                  <Form.Label className='mb-1 small'>{intl.formatMessage(tM.color)}</Form.Label>
                  <Form.Control
                    type='color'
                    value={formData.color}
                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                    style={{width: '44px', height: '38px', padding: '2px'}}
                    title={formData.color}
                  />
                </Form.Group>
                {isSuperAdmin && (
                  <Form.Group style={{flex: '1 1 200px'}}>
                    <Form.Label className='mb-1 small'>{intl.formatMessage(tM.scope)}</Form.Label>
                    <Form.Select
                      value={formData.organizationId}
                      onChange={(e) => setFormData({...formData, organizationId: e.target.value})}
                    >
                      <option value=''>{intl.formatMessage(tM.global)}</option>
                      {organizations.map((org) => (
                        <option key={org.id} value={String(parseId(org.id))}>
                          {org.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}
                <div className='d-flex gap-2'>
                  <Button type='submit' variant='primary' disabled={saving}>
                    {intl.formatMessage(editingTag ? tM.update : tM.create)}
                  </Button>
                  {editingTag && (
                    <Button type='button' variant='secondary' onClick={handleCancel}>
                      <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
                    </Button>
                  )}
                </div>
              </div>
            </Form>
          </div>

          <div className='tags-list'>
            <h6 className='mb-3'>{intl.formatMessage(tM.existing, {count: tags.length})}</h6>
            {loading ? (
              <div className='text-center py-4'>
                <div className='spinner-border spinner-border-sm' />
              </div>
            ) : tags.length === 0 ? (
              <Alert variant='info'>{intl.formatMessage(tM.empty)}</Alert>
            ) : (
              <Table striped hover size='sm'>
                <thead>
                  <tr>
                    <th style={{width: '40px'}}>{intl.formatMessage(tM.color)}</th>
                    <th><FormattedMessage id='common.field.name' defaultMessage='Name' /></th>
                    {isSuperAdmin && <th>{intl.formatMessage(tM.scope)}</th>}
                    <th style={{width: '120px'}}><FormattedMessage id='common.field.actions' defaultMessage='Actions' /></th>
                  </tr>
                </thead>
                <tbody>
                  {tags.map((tag) => {
                    const manageable = canManage(tag)
                    const busy = busyTagId === tag.id
                    return (
                      <tr key={tag.id}>
                        <td>
                          <span
                            className='badge rounded-pill px-2'
                            style={{backgroundColor: tag.color, color: getContrastColor(tag.color)}}
                          >
                            {tag.name}
                          </span>
                        </td>
                        <td>
                          <strong>{tag.name}</strong>
                        </td>
                        {isSuperAdmin && (
                          <td>
                            {tag.organization ? (
                              <Badge bg='secondary' text='light'>
                                {tag.organization.name}
                              </Badge>
                            ) : (
                              <Badge bg='dark' text='light' className='border border-secondary'>
                                {intl.formatMessage(tM.global)}
                              </Badge>
                            )}
                          </td>
                        )}
                        <td>
                          <div className='d-flex gap-1 align-items-center'>
                            <Button
                              size='sm'
                              variant='outline-primary'
                              disabled={!manageable || busy}
                              title={manageable ? intl.formatMessage(tM.editTooltip) : intl.formatMessage(tM.noPermEdit)}
                              onClick={() => handleEdit(tag)}
                            >
                              <FormattedMessage id='common.action.edit' defaultMessage='Edit' />
                            </Button>
                            <Button
                              size='sm'
                              variant='outline-danger'
                              disabled={!manageable || busy}
                              title={manageable ? intl.formatMessage(tM.deleteTooltip) : intl.formatMessage(tM.noPermDelete)}
                              onClick={() => handleDelete(tag)}
                            >
                              <FormattedMessage id='common.action.delete' defaultMessage='Delete' />
                            </Button>
                            {busy && (
                              <Spinner
                                animation='border'
                                size='sm'
                                title={intl.formatMessage(tM.checkingUsage)}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onHide}>
            <FormattedMessage id='common.action.close' defaultMessage='Close' />
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(pending)} onHide={() => setPending(null)} centered>
        {pending && (
          <>
            <Modal.Header closeButton>
              <Modal.Title className='fs-5'>
                {intl.formatMessage(pending.mode === 'edit' ? tM.confirmEditTitle : tM.confirmDeleteTitle)}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className='mb-3'>
                <span
                  className='badge rounded-pill px-2 me-2'
                  style={{backgroundColor: pending.tag.color, color: getContrastColor(pending.tag.color)}}
                >
                  {pending.tag.name}
                </span>
              </p>

              {pending.mode === 'edit' ? (
                <Alert variant='warning' className='mb-3'>
                  {intl.formatMessage(tM.confirmEditBody, {orgs: orgCount(pending.usage)})}
                </Alert>
              ) : (
                <Alert variant={pending.usage.inUse ? 'danger' : 'secondary'} className='mb-3'>
                  {intl.formatMessage(
                    pending.usage.inUse ? tM.confirmDeleteInUse : tM.confirmDeleteUnused
                  )}
                </Alert>
              )}

              {pending.usage.inUse && renderUsageBreakdown(pending.usage)}
            </Modal.Body>
            <Modal.Footer>
              <Button variant='secondary' onClick={() => setPending(null)}>
                <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
              </Button>
              <Button
                variant={pending.mode === 'edit' ? 'primary' : 'danger'}
                onClick={handleConfirm}
              >
                {intl.formatMessage(
                  pending.mode === 'edit'
                    ? tM.continueAction
                    : pending.usage.inUse
                    ? tM.deleteAnyway
                    : tM.confirmDeleteTitle
                )}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </>
  )
}
